import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { Drawer, useToast } from "@/components/system";
import {
  FormInput,
  FormTextarea,
  PrimaryButton,
  SecondaryButton,
  DialogFooter,
  Row,
  browserTz,
  localNowDatetime,
} from "@/components/forms";
import { formatError } from "@/lib/format-error";

/**
 * BW10 — User-created calendar events. Writes to public.calendar_event with
 * source='manual'. Defaults to a 1h block starting at the next half-hour.
 */

export function NewEventModal({
  open,
  defaultStart,
  onClose,
}: {
  open: boolean;
  defaultStart?: Date;
  onClose: () => void;
}) {
  const toast = useToast();
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();

  const [title, setTitle] = useState("");
  const [starts, setStarts] = useState(localNowDatetime());
  const [ends, setEnds] = useState(localNowDatetime());
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [allDay, setAllDay] = useState(false);

  useEffect(() => {
    if (!open) return;
    const start = defaultStart ?? nextHalfHour();
    const end = new Date(start);
    end.setHours(end.getHours() + 1);
    setStarts(toLocalDatetime(start));
    setEnds(toLocalDatetime(end));
    setTitle("");
    setLocation("");
    setDescription("");
    setAllDay(false);
  }, [open, defaultStart]);

  const create = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      if (!title.trim()) throw new Error("Title is required");
      const startIso = new Date(starts).toISOString();
      const endIso = new Date(ends).toISOString();
      // calendar_event types not regen'd yet — cast through unknown.
      const insert = (supabase as unknown as {
        from: (t: string) => ReturnType<typeof supabase.from>;
      }).from("calendar_event");
      const { error } = await insert.insert({
        user_id: userId,
        starts_at: startIso,
        ends_at: endIso,
        tz: browserTz(),
        title: title.trim(),
        description,
        location,
        all_day: allDay,
        source: "manual",
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["calendar_events"] });
      toast.show("Event created.");
      onClose();
    },
    onError: (e) => toast.show(formatError(e)),
  });

  return (
    <Drawer open={open} onClose={onClose} title="New event">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
      >
        <FormInput
          label="Title"
          value={title}
          onChange={setTitle}
          placeholder="Coffee with Andy"
          autoFocus
        />
        <Row>
          <FormInput label="Starts" type="datetime-local" value={starts} onChange={setStarts} />
          <FormInput label="Ends" type="datetime-local" value={ends} onChange={setEnds} />
        </Row>
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            color: "var(--fg-2)",
            fontSize: "var(--type-body-s)",
          }}
        >
          <input
            type="checkbox"
            checked={allDay}
            onChange={(e) => setAllDay(e.target.checked)}
          />
          All day
        </label>
        <FormInput
          label="Location"
          value={location}
          onChange={setLocation}
          placeholder="Optional — address or link"
        />
        <FormTextarea label="Notes" value={description} onChange={setDescription} rows={3} />

        <DialogFooter>
          <SecondaryButton onClick={onClose} disabled={create.isPending}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={create.isPending || !title.trim()}>
            {create.isPending ? "Saving…" : "Create event"}
          </PrimaryButton>
        </DialogFooter>
      </form>
    </Drawer>
  );
}

function nextHalfHour(): Date {
  const d = new Date();
  const min = d.getMinutes();
  d.setMinutes(min < 30 ? 30 : 60, 0, 0);
  return d;
}

function toLocalDatetime(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}
